import { useEffect, useMemo, useRef } from "react";
import * as FRAGS from "@thatopen/fragments";
import * as THREE from "three";
import { toast } from "react-toastify";
import { ImgLoader } from "@treegram/loaders-img";
import { FragmentLoader } from "@treegram/loaders-fragment";
import { _3drtgmLoader } from "@treegram/loaders-3drtgm";
import { _3dtgmLoader } from "@treegram/loaders-3dtgm";

import useProjectContext from "@/services/context/project/use-project-context.ts";
import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context.ts";
import useProjectIndicatorsContext from "@/pages/takeoff/context/project-indicators/use-project-indicators-context.ts";
import ENDPOINTS from "@/services/endpoints.ts";
import useViewerItemsVisibilityContext
    from "@/services/context/viewer/items-visibility/use-viewer-items-visibility-context.ts";
import KnownScaleContent from "@/pages/takeoff/context/project-organisation/models/known-scale-content.ts";
import UserDefinedContent from "@/pages/takeoff/context/project-organisation/models/user-defined-content.ts";
import FileType from "@/models/enums/file-type.ts";
import PathElementDto from "@/models/interfaces/dtos/path-element-dto.ts";
import FileRecord from "@/pages/takeoff/context/project-measure/models/file-record.ts";
import { ViewerLoader } from "@/hooks/viewer/use-viewer.ts";
import FragmentRecord from "@/pages/takeoff/context/project-measure/models/fragment-record.ts";

type PlanPdfItem = {
    storeyId: string;
    file: FileRecord;
};

type PlanFragItem = {
    storeyId: string;
    file: FragmentRecord;
};

export const useMeasureOverviewLoaders = (): ViewerLoader[] => {
    const { project } = useProjectContext();
    const { projectMeasures, storeysFileData, storeysFragsData, buildingsFileData } = useProjectMeasureContext();
    const { initialiseVisibleItems } = useViewerItemsVisibilityContext();
    const { filesPathData } = useProjectIndicatorsContext();

    const sitePositionRef = useRef<THREE.Vector3 | undefined>();
    const buildingsRef = useRef<Map<string, FileRecord[]>>(buildingsFileData);

    useEffect(() => {
        const fileIds = Array.from(storeysFileData.values()).flat().map(file => file.fileId);
        const geometries = projectMeasures.buildings.flatMap(b => b.storeys).flatMap(s => s.measures).flatMap(m => m.geometryRepresentations).map(g => g.geometryId);

        const visibleItems = fileIds.concat(geometries);
        initialiseVisibleItems(visibleItems);
    }, [storeysFileData, projectMeasures, initialiseVisibleItems]);

    useEffect(() => {
        buildingsRef.current = buildingsFileData;
    }, [buildingsFileData]);

    const planPdfItems = useMemo<PlanPdfItem[]>(
        () => Array.from(storeysFileData.entries()).flatMap(([storeyId, files]) =>
            files.map((file) => ({ storeyId, file }))
        ),
        [storeysFileData],
    );

    const planFragItems = useMemo<PlanFragItem[]>(
        () => Array.from(storeysFragsData.entries()).flatMap(([storeyId, files]) =>
            files.map((file) => ({ storeyId, file }))
        ),
        [storeysFragsData],
    );

    useEffect(() => {
        const firstFile3D = Array.from(buildingsFileData.values())
            .map(files =>
                files.find(f => f.fileType === FileType.File3D && f.sitePosition != null)
            )
            .find(Boolean);

        if (firstFile3D?.sitePosition) {
            sitePositionRef.current = firstFile3D.sitePosition;
        }
    }, [buildingsFileData]);

    const planPdfLoader = useMemo<ViewerLoader<unknown, ImgLoader, PlanPdfItem>>(() => {
        const lookAtPosition = new THREE.Vector3(0, 0, 0);
        const dimensions: Map<string, { width: number, height: number }> = new Map();
        const organisationId = project.projectOrganisation.id;
        const projectId = project.id;

        return {
            key: "plan-pdf-loader",
            factory: (ctx) => new ImgLoader(ctx.viewer.getComponents(), ctx.world),
            name: ({ file }) => file.fileId,
            itemsToLoad: planPdfItems,
            fetchUrl: ({ file }) => ENDPOINTS.VIEWER_PDF_PREVIEW(projectId, `${organisationId}/${projectId}/${file.relativePath}`, true, false),
            getOptions: ({ storeyId, file }) => {
                const storey = projectMeasures.getStorey(storeyId);

                let loaderOptions = {
                    origin: new THREE.Vector2(file.origin.position.x, file.origin.position.y),
                    direction: file.origin.direction,
                    altimetry: storey?.altimetry,
                    transparent: true,
                    opacity: 1
                };

                if (file.sitePosition) {
                    loaderOptions.origin = new THREE.Vector2(
                        file.origin.position.x + file.sitePosition.x - (sitePositionRef.current?.x ?? 0),
                        file.origin.position.y + file.sitePosition.y - (sitePositionRef.current?.y ?? 0)
                    );
                    loaderOptions.altimetry = (storey?.altimetry ?? 0) - (sitePositionRef.current?.z ?? 0);
                }

                if (file.content instanceof KnownScaleContent) {
                    loaderOptions = {
                        ...loaderOptions,
                        ...{ format: file.content.format, orientation: file.content.orientation, scale: file.content.scale }
                    };
                }
                else if (file.content instanceof UserDefinedContent) {
                    loaderOptions = {
                        ...loaderOptions,
                        ...{ definedScale: file.content.scale }
                    };
                }

                lookAtPosition.x = loaderOptions.origin.x;
                lookAtPosition.y = loaderOptions.altimetry ?? 0;
                lookAtPosition.z = -loaderOptions.origin.y;

                return { useCoordinates: false, loaderOptions };
            },
            onItemLoaded: (_ctx, loader, { storeyId, file }) => {
                const dimensionsCanvas = loader.getDimensionsCanvas();
                if (dimensionsCanvas) {
                    file.setDimensions(dimensionsCanvas.width, dimensionsCanvas.height);
                    dimensions.set(storeyId, { width: dimensionsCanvas.width, height: dimensionsCanvas.height });
                }
            },
            onAllItemsLoaded: async (ctx) => {
                const maxDimension = dimensions.values().reduce((max, r) => {
                    const { width, height } = r;
                    const largestSide = Math.max(width, height);

                    return Math.max(max, largestSide);
                }, 0);

                if (maxDimension > 0) {
                    await ctx.viewer.getCamera().controls.setLookAt(lookAtPosition.x + maxDimension * 0.7, lookAtPosition.y + maxDimension * 0.35, lookAtPosition.z + maxDimension * 0.7, lookAtPosition.x, lookAtPosition.y, lookAtPosition.z);
                }
            }
        };
    }, [planPdfItems, project.id, project.projectOrganisation.id, projectMeasures]);

    const fragLoader = useMemo<ViewerLoader<unknown, FragmentLoader, PlanFragItem>>(
        () => ({
            key: "frag-loader",
            factory: (ctx) => new FragmentLoader(ctx.viewer.getComponents(), ctx.world),
            itemsToLoad: planFragItems,
            fetchUrl: ({ file }) => ENDPOINTS.VIEWER_FILE(file.filePath),
            name: ({ file }) => `${crypto.randomUUID()}_${file.name}`,
            getOptions: ({ file, storeyId }) => {
                const storey = projectMeasures.getStorey(storeyId);

                const loaderOptions = {
                    lodMode: FRAGS.LodMode.ALL_GEOMETRY,
                    origin: new THREE.Vector2(file.origin.position.x, file.origin.position.y),
                    direction: file.origin.direction,
                    altimetry: storey?.altimetry,
                    showEdges: false
                };

                if (file.sitePosition) {
                    loaderOptions.origin = new THREE.Vector2(file.origin.position.x + file.sitePosition.x - (sitePositionRef.current?.x ?? 0), file.origin.position.y + file.sitePosition.y - (sitePositionRef.current?.y ?? 0));
                    loaderOptions.altimetry = (storey?.altimetry ?? 0) - (sitePositionRef.current?.z ?? 0);
                }

                return { useCoordinates: false, loaderOptions };
            },
            onError: (_ctx, _loader, { file }) => {
                toast.error(`Erreur lors du chargement des fichiers 3D : ${file.name}`, { autoClose: false });
            },
        }),
        [planFragItems, projectMeasures]
    );

    const boomer3drtgmLoader = useMemo<ViewerLoader<unknown, _3drtgmLoader, PathElementDto>>(() => {
        const pathKeys: string[] = [];
        projectMeasures.buildings.forEach(b => {
            b.storeys.forEach(s => {
                const buildingName = b.name.replace(/ /g, "_");
                const storeyName = s.name.replace(/ /g, "_");
                const pathKey = `${buildingName}_${storeyName}`;
                pathKeys.push(pathKey);
            });
        });

        const datas = filesPathData?.filter((d) =>
            pathKeys.some((pathKey) => d.name.includes(pathKey)) && d.path.endsWith(".3drtgm")
        ) ?? [];

        return {
            key: "boomer-3drtgm-loader",
            factory: (ctx) => new _3drtgmLoader(ctx.viewer.getComponents(), ctx.world),
            itemsToLoad: datas,
            fetchUrl: (path) => ENDPOINTS.VIEWER_FILE_BOOMER(path.path),
            name: (path) => path.name,
            getOptions: () => ({ 
                useCoordinates: false, 
                loaderOptions: { lodMode: FRAGS.LodMode.ALL_GEOMETRY, loadHidden: true, showEdges: false } 
            })
        };
    }, [filesPathData, projectMeasures.buildings]);

    const boomer3dtgmLoader = useMemo<ViewerLoader<unknown, _3dtgmLoader, PathElementDto>>(() => {
        const pathKeys: string[] = [];
        projectMeasures.buildings.forEach(b => {
            b.storeys.forEach(s => {
                const buildingName = b.name.replace(/ /g, "_");
                const storeyName = s.name.replace(/ /g, "_");
                const pathKey = `${buildingName}_${storeyName}`;
                pathKeys.push(pathKey);
            });
        });

        const datas = filesPathData?.filter((d) =>
            pathKeys.some((pathKey) => d.name.includes(pathKey)) && d.path.endsWith(".3dtgm")
        ) ?? [];

        return {
            key: "boomer-3dtgm-loader",
            factory: (ctx) => new _3dtgmLoader(ctx.viewer.getComponents(), ctx.world),
            itemsToLoad: datas,
            fetchUrl: (path) => ENDPOINTS.VIEWER_FILE_BOOMER(path.path),
            name: (path) => path.name,
            getOptions: () => ({ 
                useCoordinates: false, 
                loaderOptions: { lodMode: FRAGS.LodMode.ALL_GEOMETRY, loadHidden: true, showEdges: false } 
            })
        };
    }, [filesPathData, projectMeasures.buildings]);

    return useMemo<ViewerLoader[]>(
        () => [planPdfLoader, fragLoader, boomer3drtgmLoader, boomer3dtgmLoader],
        [planPdfLoader, fragLoader, boomer3drtgmLoader, boomer3dtgmLoader]
    );
};

export default useMeasureOverviewLoaders;
